import React, { useState, useEffect } from 'react';
import LoanForm from './LoanForm';
import Popup from './ui/Popup';
import Alert from './ui/Alert';
import { LoanType } from '../types';
import { t } from '../utils/i18n';
import { 
  Home, 
  Briefcase, 
  Building, 
  Wheat, 
  RefreshCw, 
  Car, 
  Check 
} from 'lucide-react';

const LoanTypesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('mortgage');
  const [isMobile, setIsMobile] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  
  // Map tab names to loan type IDs
  const tabToLoanTypeMap: { [key: string]: LoanType } = {
    'mortgage': 'mortgage',
    'business': 'business',
    'construction': 'construction',
    'farmers': 'farmers',
    'consolidation': 'consolidation',
    'auto': 'auto'
  };

  const loanTypes = {
    mortgage: {
      title: t('loan_types.mortgage.title'),
      description: t('loan_types.mortgage.description'),
      features: t('loan_types.mortgage.features', { returnObjects: true }) as unknown as string[],
      requirements: t('loan_types.mortgage.requirements', { returnObjects: true }) as unknown as string[],
      icon: <Home className="w-8 h-8" />
    },
    business: {
      title: t('loan_types.business.title'),
      description: t('loan_types.business.description'),
      features: t('loan_types.business.features', { returnObjects: true }) as unknown as string[],
      requirements: t('loan_types.business.requirements', { returnObjects: true }) as unknown as string[],
      icon: <Briefcase className="w-8 h-8" />
    },
    construction: {
      title: t('loan_types.construction.title'),
      description: t('loan_types.construction.description'),
      features: t('loan_types.construction.features', { returnObjects: true }) as unknown as string[],
      requirements: t('loan_types.construction.requirements', { returnObjects: true }) as unknown as string[],
      icon: <Building className="w-8 h-8" />
    },
    farmers: {
      title: t('loan_types.farm.title'),
      description: t('loan_types.farm.description'),
      features: t('loan_types.farm.features', { returnObjects: true }) as unknown as string[],
      requirements: t('loan_types.farm.requirements', { returnObjects: true }) as unknown as string[],
      icon: <Wheat className="w-8 h-8" />
    },
    consolidation: {
      title: t('loan_types.consolidation.title'),
      description: t('loan_types.consolidation.description'),
      features: t('loan_types.consolidation.features', { returnObjects: true }) as unknown as string[],
      requirements: t('loan_types.consolidation.requirements', { returnObjects: true }) as unknown as string[],
      icon: <RefreshCw className="w-8 h-8" />
    },
    auto: {
      title: t('loan_types.auto.title'),
      description: t('loan_types.auto.description'),
      features: t('loan_types.auto.features', { returnObjects: true }) as unknown as string[],
      requirements: t('loan_types.auto.requirements', { returnObjects: true }) as unknown as string[],
      icon: <Car className="w-8 h-8" />
    }
  };

  const tabs = [
    { id: 'mortgage', label: t('loan_types.mortgage.title') },
    { id: 'business', label: t('loan_types.business.title') },
    { id: 'construction', label: t('loan_types.construction.title') },
    { id: 'farmers', label: t('loan_types.farm.title') },
    { id: 'consolidation', label: t('loan_types.consolidation.title') },
    { id: 'auto', label: t('loan_types.auto.title') }
  ];

  const currentLoan = loanTypes[activeTab as keyof typeof loanTypes];

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle URL hash changes for anchor navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      // Map anchor links to loan type IDs
      const anchorToId: { [key: string]: string } = {
        'hipotekarais-kredits': 'mortgage',
        'biznesa-attistibai': 'business',
        'kreditu-apvienosana': 'consolidation',
        'auto-lizings': 'auto',
        'zemniekiem': 'farmers',
        'eku-buvniecibai': 'construction'
      };
      
      if (hash && anchorToId[hash]) {
        setActiveTab(anchorToId[hash]);
      } else if (hash && ['mortgage', 'business', 'consolidation', 'auto', 'farmers', 'construction'].includes(hash)) {
        setActiveTab(hash);
      }
    };

    // Set initial tab from URL hash
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Update URL hash when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Map loan type IDs to anchor links
    const idToAnchor: { [key: string]: string } = {
      'mortgage': 'hipotekarais-kredits',
      'business': 'biznesa-attistibai',
      'consolidation': 'kreditu-apvienosana',
      'auto': 'auto-lizings',
      'farmers': 'zemniekiem',
      'construction': 'eku-buvniecibai'
    };
    
    const anchor = idToAnchor[tabId] || tabId;
    window.history.pushState(null, '', `#${anchor}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6" dangerouslySetInnerHTML={{ __html: t('loan_types.page_title') }} />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('loan_types.page_description')}
            </p>
          </div>
        </div>
      </section>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Alert 
            type="success" 
            message={t('home.loan_types_page.application_submitted')} 
            visible={true}
            className="mb-6"
            autoClose
            autoCloseTime={5000}
            onClose={() => setShowSuccessAlert(false)}
          />
        </div>
      )}

      {/* Loan Types Tabs */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-primary-50 hover:text-primary-500 shadow-md'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Active Loan Type Content */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Side - Info */}
              <div className="p-8 lg:p-12">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                    {currentLoan.icon}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{currentLoan.title}</h2>
                    <p className="text-gray-600">{currentLoan.description}</p>
                  </div>
                </div>

                <div className="space-y-8">
                  {/* Features */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('home.loan_types_page.main_advantages')}</h3>
                    <ul className="space-y-3">
                      {currentLoan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('home.loan_types_page.required_documents')}</h3>
                    <ul className="space-y-3">
                      {currentLoan.requirements.map((requirement, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right Side - Application Form */}
              <div className="bg-gray-50 p-6 lg:p-8">
                <div className="h-full">
                  {isMobile ? (
                    <div className="text-center space-y-6">
                      <div className="mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {t('home.loan_types_page.apply_for_loan')} {currentLoan.title.toLowerCase()}
                        </h3>
                        <p className="text-gray-600">
                          {t('home.loan_types_page.fill_application')}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => setShowMobilePopup(true)}
                        className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-300 shadow-lg hover:shadow-xl"
                      >
                        {t('home.loan_types_page.apply_now')}
                      </button>
                      
                      <div className="grid grid-cols-1 gap-4 mt-8">
                        <div className="flex items-center space-x-3">
                          <Check className="w-6 h-6 text-green-500" />
                          <span className="text-gray-700">{t('home.loan_types_page.fast_approval')}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Check className="w-6 h-6 text-green-500" />
                          <span className="text-gray-700">{t('home.loan_types_page.flexible_terms')}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Check className="w-6 h-6 text-green-500" />
                          <span className="text-gray-700">{t('home.loan_types_page.personal_approach')}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <LoanForm 
                      onSubmit={(formData) => {
                        console.log('Form submitted:', formData);
                        setShowSuccessAlert(true);
                        setTimeout(() => setShowSuccessAlert(false), 5000);
                      }}
                      onLoading={(loading, message) => {
                        console.log('Form loading:', loading, message);
                      }}
                      defaultLoanType={tabToLoanTypeMap[activeTab]}
                      isInPopup={true}
                      onClose={() => {/* This form is not in a popup, so no close action needed */}}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('home.loan_types_page.why_choose_finlo')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.loan_types_page.trusted_partner')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('home.loan_types_page.fast_approval')}</h3>
              <p className="text-gray-600">
                {t('home.loan_types_page.fast_approval_description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('home.loan_types_page.competitive_rates')}</h3>
              <p className="text-gray-600">
                {t('home.loan_types_page.competitive_rates_description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('home.loan_types_page.personal_approach')}</h3>
              <p className="text-gray-600">
                {t('home.loan_types_page.personal_approach_description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Popup */}
      <Popup show={showMobilePopup} onClose={() => setShowMobilePopup(false)}>
        <LoanForm 
          onSubmit={(formData) => {
            console.log('Form submitted:', formData);
            setShowMobilePopup(false);
            setShowSuccessAlert(true);
            setTimeout(() => setShowSuccessAlert(false), 5000);
          }}
          onLoading={(loading, message) => {
            console.log('Form loading:', loading, message);
          }}
          defaultLoanType={tabToLoanTypeMap[activeTab]}
          isInPopup={true}
          onClose={() => setShowMobilePopup(false)}
        />
      </Popup>
    </div>
  );
};

export default LoanTypesPage;
